<template>
  <main class="v2-processor-page">
    <section v-if="loading && !request" class="v2-processor-boot" role="status" aria-live="polite">
      <div class="v2-processor-boot__accent"></div>
      <div class="v2-processor-boot__content">
        <div class="v2-processor-boot__spinner" aria-hidden="true"></div>
        <div>
          <p class="v2-processor-eyebrow">Opening secure workspace</p>
          <h2>Opening request…</h2>
          <p>Loading the latest saved state.</p>
        </div>
        <p class="v2-processor-boot__fine">The paid request remains available if this tab is closed.</p>
      </div>
    </section>

    <section v-else-if="errorMessage && !request && !requestDeleted" class="v2-processor-panel v2-processor-panel--error">
      <h2>Unable to open this request</h2>
      <p>{{ errorMessage }}</p>
      <button class="v2-processor-button" type="button" @click="refreshRequest(true)">Try again</button>
    </section>

    <section v-else-if="requestDeleted" class="v2-processor-panel v2-processor-panel--deleted" role="status" aria-live="polite">
      <div>
        <p class="v2-processor-eyebrow">Request removed</p>
        <h2>Processing stopped</h2>
        <p>{{ requestDeletionMessage }}</p>
        <p class="v2-processor-reference">Request {{ requestUuid.slice(0, 8) }}</p>
      </div>
      <div class="v2-processor-actions">
        <button class="v2-processor-button v2-processor-button--primary" type="button" @click="returnToAnalysis">
          Back to requests
        </button>
        <button class="v2-processor-button" type="button" @click="closeDeletedProcessorTab">
          Close tab
        </button>
      </div>
    </section>

    <template v-else-if="request">
      <section v-if="inBatchMode" class="v2-processor-batch-context" aria-label="CSV batch processing context">
        <div>
          <p class="v2-processor-eyebrow">CSV batch processing</p>
          <strong>This request is running inside a persistent sequential batch.</strong>
          <span>When it reaches a terminal state, this tab will continue to the next queued request automatically.</span>
        </div>
        <button class="v2-processor-button" type="button" @click="returnToBatch">
          View batch queue
        </button>
      </section>

      <section v-if="['report', 'result'].includes(requestedView) && reportSummary" class="v2-processor-report">
        <header>
          <p class="v2-processor-eyebrow">Analysis report</p>
          <h2>Saved PathTracer analysis</h2>
          <p>{{ request.status_message || 'Completed PathTracer analysis.' }}</p>
          <p v-if="processorDebugAllowed && processorDebugVisible" class="v2-processor-reference">
            Request {{ request.display_id || request.request_uuid.slice(0, 8) }}
          </p>
        </header>
        <div class="v2-processor-report__metrics">
          <div><span>Resolved</span><strong>{{ reportCoverage.resolvedPercent }}%</strong></div>
          <div><span>Resolved amount</span><strong>{{ reportResolvedAmountDisplay }}</strong></div>
          <div><span>Unresolved amount</span><strong>{{ reportUnresolvedAmountDisplay }}</strong></div>
          <div><span>Endpoints</span><strong>{{ reportEndpoints.length }}</strong></div>
        </div>
        <div v-if="reportEndpoints.length" class="v2-processor-report__table-wrap">
          <table class="v2-processor-report__table">
            <thead><tr><th>Path</th><th>Address</th><th>Entity</th><th>Amount</th><th>Category</th></tr></thead>
            <tbody>
              <tr v-for="(entry, index) in reportEndpoints" :key="`${entry.hash || entry.to || 'endpoint'}-${index}`">
                <td>{{ entry.pathLabel || '—' }}</td>
                <td class="v2-processor-mono">{{ entry.to || '—' }}</td>
                <td>{{ entry.entityLabel || entry.toAttribution || 'Unattributed' }}</td>
                <td>{{ formatReportEntryAmount(entry) }}</td>
                <td>
                  <span class="v2-report-category" :class="`v2-report-category--${reportCategoryKey(entry)}`">
                    <span class="v2-report-category__flag" aria-hidden="true"></span>
                    {{ reportCategoryLabel(entry) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section
        v-if="reportOnlyView && !reportSummary"
        class="v2-processor-panel v2-processor-panel--warning"
        role="status"
      >
        <div>
          <p class="v2-processor-eyebrow">Analysis report</p>
          <h2>Report not available yet</h2>
          <p>The request has not stored a completed report summary.</p>
        </div>
      </section>

      <section
        v-if="!reportOnlyView"
        class="v2-processor-workspace"
        aria-label="PathTracer live analysis workspace"
      >
      <section v-if="!processorMountReady" class="v2-processor-boot v2-processor-boot--compact" role="status" aria-live="polite">
        <div class="v2-processor-boot__accent"></div>
        <div class="v2-processor-boot__content">
          <div class="v2-processor-boot__spinner" aria-hidden="true"></div>
          <div>
            <p class="v2-processor-eyebrow">Request ready</p>
            <h2>Starting processor…</h2>
            <p>{{ processorStartupMessage }}</p>
          </div>
          <p class="v2-processor-boot__fine">Latest checkpoint secured.</p>
        </div>
      </section>

      <CaseBuddyLite
        v-else
        ref="caseBuddyProcessor"
        :key="`${request.request_uuid}:${processorMountGeneration}`"
        :incoming-request="request"
        :username="username"
        :managed-request-mode="true"
        :view-only="viewOnly"
        :auto-run-incoming="autoStart"
        :auto-start-managed-request="autoStart"
        :persist-requests="!adminMode"
        :show-run-log="processorDebugVisible"
        :show-automation-callouts="processorDebugVisible"
        :managed-resume-state-max-bytes="processorResumeStateLimit"
        :request-api-url="lifecycleApiUrl"
        @managed-ready="handleManagedReady"
        @request-updated="handleRequestUpdated"
        @lease-acquired="handleLeaseAcquired"
        @lease-conflict="handleLeaseConflict"
        @complete="handleComplete"
        @guardrail="handleGuardrail"
        @request-deleted="handleRequestDeleted"
        @error="handleProcessorError"
      />
      </section>

      <section v-if="processorIssue" class="v2-processor-panel v2-processor-panel--warning" role="alert">
        <div>
          <p class="v2-processor-eyebrow">Processor recovery</p>
          <h2>Trace workspace paused safely</h2>
          <p>{{ processorIssue.message }}</p>
          <p v-if="processorIssue.reference" class="v2-processor-reference">
            Reference {{ processorIssue.reference }}
          </p>
        </div>
        <div class="v2-processor-actions">
          <button class="v2-processor-button v2-processor-button--primary" type="button" :disabled="processorRecoveryInFlight" @click="retryProcessor">
            {{ processorRecoveryInFlight ? 'Reconnecting…' : 'Reconnect processor' }}
          </button>
          <button class="v2-processor-button" type="button" :disabled="loading" @click="refreshRequest(true)">
            Refresh status
          </button>
          <button class="v2-processor-button" type="button" @click="dismissProcessorIssue">
            Dismiss
          </button>
        </div>
      </section>

      <section class="v2-processor-status" :class="`v2-processor-status--${statusClass}`">
        <div class="v2-processor-status__primary">
          <strong>{{ statusLabel }}</strong>
          <span>{{ simplifiedStatusMessage }}</span>
        </div>
        <div class="v2-processor-status__meta">
          <span>{{ displayProgressPercent }}%</span>
          <span v-if="request.lease_active && !childLeaseOwned">Processing in another tab</span>
          <span v-if="processorDebugAllowed && processorDebugVisible">Request {{ request.display_id || request.request_uuid?.slice(0, 8) }}</span>
        </div>
      </section>

      <header class="v2-processor-header v2-processor-header--context">
        <div>
          <p class="v2-processor-eyebrow">{{ adminMode ? 'PathTracer staff request review' : 'PathTracer analysis request' }}</p>
          <h1>{{ pageTitle }}</h1>
          <p class="v2-processor-subtitle">
            {{ adminMode ? 'Read-only support view. No processing lease or credit change can occur from this page.' : 'The accepted request and its checkpoints remain stored on the server. Closing this tab does not create another charge.' }}
          </p>
        </div>
        <div class="v2-processor-actions">
          <button
            v-if="processorDebugAllowed"
            class="v2-processor-button v2-processor-button--debug"
            type="button"
            @click="toggleProcessorDebug"
          >
            {{ processorDebugVisible ? 'Hide diagnostics' : 'Show diagnostics' }}
          </button>
          <button class="v2-processor-button" type="button" @click="refreshRequest(true)" :disabled="loading">
            Refresh
          </button>
          <button class="v2-processor-button" type="button" @click="returnToAnalysis">
            {{ adminMode ? 'Back to request monitor' : 'Back to requests' }}
          </button>
        </div>
      </header>

      <section
        v-if="processorDebugAllowed && processorDebugVisible"
        class="v2-processor-panel v2-processor-panel--debug"
        aria-label="Processor diagnostics"
      >
        <header class="v2-processor-debug__header">
          <div>
            <p class="v2-processor-eyebrow">Testing diagnostics</p>
            <h2>Processor and checkpoint state</h2>
            <p>Visible only to current administrator and moderator accounts.</p>
          </div>
          <div class="v2-processor-actions">
            <button class="v2-processor-button" type="button" @click="refreshProcessorDiagnostics">
              Refresh diagnostics
            </button>
            <button class="v2-processor-button v2-processor-button--primary" type="button" @click="copyProcessorDiagnostics">
              Copy diagnostics
            </button>
          </div>
        </header>

        <p v-if="processorDebugCopyStatus" class="v2-processor-debug__status">
          {{ processorDebugCopyStatus }}
        </p>

        <div class="v2-processor-debug__grid">
          <div><span>Service</span><strong>{{ processorDebugMeta.service_version || '—' }}</strong></div>
          <div><span>Request status</span><strong>{{ request.status || '—' }}</strong></div>
          <div><span>State version</span><strong>{{ request.state_version ?? '—' }}</strong></div>
          <div><span>Progress</span><strong>{{ Number(request.progress_percent || 0).toFixed(1) }}%</strong></div>
          <div><span>Stored resume JSON</span><strong>{{ formatBytes(processorDebugMeta.stored_resume_state_json_bytes ?? request.resume_state_bytes) }}</strong></div>
          <div><span>Stored gzip/base64</span><strong>{{ formatBytes(processorDebugMeta.stored_resume_state_base64_bytes) }}</strong></div>
          <div><span>Effective raw JSON ceiling</span><strong>{{ formatBytes(processorDebugMeta.resume_state_max_json_bytes) }}</strong></div>
          <div><span>Configured raw target</span><strong>{{ formatBytes(processorDebugMeta.resume_state_configured_json_bytes) }}</strong></div>
          <div><span>Stored text ceiling</span><strong>{{ formatBytes(processorDebugMeta.resume_state_max_stored_base64_bytes) }}</strong></div>
          <div><span>PHP post_max_size</span><strong>{{ processorDebugMeta.php_post_max_size || '—' }}</strong></div>
          <div><span>PHP memory_limit</span><strong>{{ processorDebugMeta.php_memory_limit || '—' }}</strong></div>
          <div><span>Last checkpoint</span><strong>{{ request.last_checkpoint_at || '—' }}</strong></div>
          <div><span>Lease</span><strong>{{ request.lease_active ? 'Active' : 'Inactive' }}</strong></div>
        </div>

        <div v-if="processorChildDebug" class="v2-processor-debug__details">
          <h3>Current browser checkpoint</h3>
          <div class="v2-processor-debug__grid">
            <div><span>Profile</span><strong>{{ processorChildDebug.checkpoint?.diagnostics?.lastProfile || '—' }}</strong></div>
            <div><span>Last JSON size</span><strong>{{ formatBytes(processorChildDebug.checkpoint?.diagnostics?.lastJsonBytes) }}</strong></div>
            <div><span>Last stored raw size</span><strong>{{ formatBytes(processorChildDebug.checkpoint?.diagnostics?.lastStoredBytes) }}</strong></div>
            <div><span>Degraded saves</span><strong>{{ processorChildDebug.checkpoint?.diagnostics?.degradedCheckpointCount || 0 }}</strong></div>
            <div><span>Rows</span><strong>{{ processorChildDebug.request?.rowCount ?? '—' }}</strong></div>
            <div><span>Terminal entries</span><strong>{{ processorChildDebug.request?.terminalEntryCount ?? '—' }}</strong></div>
            <div><span>Visual entries</span><strong>{{ processorChildDebug.performance?.parsedEntryCount ?? '—' }}</strong></div>
            <div><span>Nodes / edges</span><strong>{{ processorChildDebug.performance ? `${processorChildDebug.performance.nodeCount} / ${processorChildDebug.performance.edgeCount}` : '—' }}</strong></div>
            <div><span>Large-flow mode</span><strong>{{ processorChildDebug.performance?.largeGraphMode ? 'Active' : 'Inactive' }}</strong></div>
            <div><span>Last graph build</span><strong>{{ formatMilliseconds(processorChildDebug.performance?.lastVisualBuildMs) }}</strong></div>
            <div><span>Slowest graph build</span><strong>{{ formatMilliseconds(processorChildDebug.performance?.maxVisualBuildMs) }}</strong></div>
            <div><span>Graph builds / skipped</span><strong>{{ processorChildDebug.performance ? `${processorChildDebug.performance.visualRebuildCount} / ${processorChildDebug.performance.visualRebuildSkipped}` : '—' }}</strong></div>
            <div><span>Last checkpoint</span><strong>{{ formatMilliseconds(processorChildDebug.performance?.lastCheckpointMs) }}</strong></div>
            <div><span>Deferred checkpoints</span><strong>{{ processorChildDebug.performance?.checkpointDeferredCount ?? '—' }}</strong></div>
            <div><span>Restore time</span><strong>{{ formatMilliseconds(processorChildDebug.performance?.restoreMs) }}</strong></div>
            <div><span>Long tasks</span><strong>{{ processorChildDebug.performance ? `${processorChildDebug.performance.longTaskCount} · max ${formatMilliseconds(processorChildDebug.performance.maxLongTaskMs)}` : '—' }}</strong></div>
          </div>
          <p v-if="processorChildDebug.checkpoint?.warning" class="v2-processor-debug__warning">
            {{ processorChildDebug.checkpoint.warning }}
          </p>
          <details v-if="processorDebugSectionRows.length">
            <summary>Checkpoint section sizes</summary>
            <table class="v2-processor-debug__table">
              <thead><tr><th>Section</th><th>JSON size</th></tr></thead>
              <tbody>
                <tr v-for="row in processorDebugSectionRows" :key="row.name">
                  <td>{{ row.name }}</td>
                  <td>{{ formatBytes(row.bytes) }}</td>
                </tr>
              </tbody>
            </table>
          </details>
        </div>
      </section>
    </template>
  </main>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import AuthService from '@/services/AuthService';
import v2BatchClient from '@/services/v2BatchClient';

const V2_REQUEST_EVENT_CHANNEL = 'pathtracer-v2-request-events';
const V2_REQUEST_EVENT_STORAGE_KEY = 'pathtracer-v2-request-event';
const REPORT_STABLECOIN_SYMBOLS = new Set(['USDT', 'USDD', 'USDC', 'DAI', 'USD']);
const REPORT_AMOUNT_PATTERN = /[-+]?(?:(?:\d[\d,]*)(?:\.\d*)?|\.\d+)(?:[eE][-+]?\d+)?/;

const CaseBuddyLite = defineAsyncComponent({
  loader: () => import('@/components/CaseBuddyLite.vue'),
  delay: 0,
  timeout: 60000,
  suspensible: false,
  onError(error, retry, fail, attempts) {
    if (attempts <= 2) {
      retry();
      return;
    }
    fail(error);
  },
});

export default {
  name: 'V2RequestProcessor',
  components: { CaseBuddyLite },
  props: {
    adminMode: {
      type: Boolean,
      default: false,
    },
    requestUuidOverride: {
      type: String,
      default: '',
    },
    viewOverride: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      request: null,
      loading: false,
      errorMessage: '',
      pollTimer: null,
      fetchInFlight: false,
      childLeaseOwned: false,
      processorMountReady: false,
      processorStartupMessage: 'Loading the durable request record.',
      processorMountTimer: null,
      processorMountGeneration: 0,
      processorIssue: null,
      processorRecoveryInFlight: false,
      requestDeleted: false,
      requestDeletionMessage: '',
      requestEventChannel: null,
      requestStorageListener: null,
      processorDebugAllowed: false,
      processorDebugVisible: false,
      processorDebugMeta: {},
      processorChildDebug: null,
      processorDebugCopyStatus: '',
      lifecycleAbortController: null,
      // Do not trust localStorage for diagnostic authorization. This is
      // populated only after AuthService verifies the current server session.
      currentUserRole: '',
      username: typeof localStorage !== 'undefined' ? (localStorage.getItem('username') || '') : '',
      batchAdvanceInFlight: false,
      batchAdvanceTimer: null,
      batchAdvanceScheduledFor: '',
      batchHeartbeatTimer: null,
      adminViewAudited: false,
    };
  },
  computed: {
    requestUuid() {
      return String(this.requestUuidOverride || this.$route.params.requestUuid || '').trim();
    },
    batchUuid() {
      return String(this.$route.query.batch || '').trim().toLowerCase();
    },
    batchItemId() {
      const value = Number(this.$route.query.batch_item || 0);
      return Number.isInteger(value) && value > 0 ? value : null;
    },
    inBatchMode() {
      if (this.adminMode) return false;
      return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(this.batchUuid);
    },
    requestedAction() {
      return String(this.$route.query.action || '').trim().toLowerCase();
    },
    requestedView() {
      return String(this.viewOverride || this.$route.query.view || '').trim().toLowerCase();
    },
    reportOnlyView() {
      return this.requestedView === 'report';
    },
    lifecycleApiUrl() {
      return this.adminMode
        ? '/api/etherscan/admin_casebuddy_lite_request.php'
        : '/api/etherscan/casebuddy_lite_request.php';
    },
    viewOnly() {
      return this.adminMode
        || this.request?.status === 'completed'
        || ['result', 'visual', 'report', 'image'].includes(this.requestedView);
    },
    autoStart() {
      return !this.adminMode
        && !this.viewOnly
        && ['start', 'resume'].includes(this.requestedAction);
    },
    pageTitle() {
      if (!this.request) return this.adminMode ? 'Opening staff request review' : 'Opening request';
      if (this.adminMode) return 'Staff request review';
      if (this.viewOnly) return 'Saved analysis result';
      if (this.request.status === 'running' && !this.childLeaseOwned) return 'Viewing active analysis progress';
      return this.request.next_action === 'resume' ? 'Resume analysis request' : 'Process analysis request';
    },
    statusClass() {
      const status = String(this.request?.status || 'queued').toLowerCase();
      if (status === 'completed') return 'complete';
      if (status === 'failed' || status === 'guardrail') return 'warning';
      if (status === 'running') return 'running';
      if (status === 'paused') return 'paused';
      return 'idle';
    },
    statusLabel() {
      const status = String(this.request?.status || 'queued').toLowerCase();
      const labels = {
        queued: 'Queued',
        running: 'In progress',
        paused: 'Paused',
        completed: 'Complete',
        guardrail: 'Needs review',
        failed: 'Recoverable error',
        cancelled: 'Abandoned',
      };
      return labels[status] || status;
    },
    displayProgressPercent() {
      const value = Number(this.request?.progress_percent || 0);
      if (!Number.isFinite(value)) return '0.0';
      return Math.max(0, Math.min(100, value)).toFixed(1);
    },
    processorResumeStateLimit() {
      const value = Number(this.processorDebugMeta?.resume_state_max_json_bytes);
      if (!Number.isFinite(value) || value < 2097152) return 12582912;
      return Math.min(25165824, Math.trunc(value));
    },
    currentUserIsStaff() {
      return ['admin', 'moderator'].includes(String(this.currentUserRole || '').toLowerCase());
    },
    processorDebugSectionRows() {
      const sections = this.processorChildDebug?.checkpoint?.diagnostics?.lastSectionBytes;
      if (!sections || typeof sections !== 'object') return [];
      return Object.entries(sections)
        .map(([name, bytes]) => ({ name, bytes: Number(bytes) || 0 }))
        .sort((left, right) => right.bytes - left.bytes);
    },
    reportSummary() {
      return this.request?.result_summary && typeof this.request.result_summary === 'object'
        ? this.request.result_summary
        : null;
    },
    reportCoverage() {
      const coverage = this.reportSummary?.coverage || {};
      const resolvedPercent = Number(coverage.resolvedPercent || 0);
      return {
        resolvedPercent: Number.isFinite(resolvedPercent)
          ? Math.max(0, Math.min(100, resolvedPercent)).toFixed(1)
          : '0.0',
        resolvedAmount: coverage.resolvedAmount ?? '—',
        unresolvedAmount: coverage.unresolvedAmount ?? '—',
      };
    },
    reportResolvedAmountDisplay() {
      return this.formatReportAmountGroups(
        this.reportEndpoints,
        this.reportCoverage.resolvedAmount
      );
    },
    reportUnresolvedAmountDisplay() {
      const openEndings = Array.isArray(this.reportSummary?.openEndings)
        ? this.reportSummary.openEndings
        : [];
      return this.formatReportAmountGroups(
        openEndings,
        this.reportCoverage.unresolvedAmount
      );
    },
    reportEndpoints() {
      return Array.isArray(this.reportSummary?.endpoints) ? this.reportSummary.endpoints : [];
    },
    simplifiedStatusMessage() {
      const status = String(this.request?.status || 'queued').toLowerCase();
      const rawMessage = String(this.request?.status_message || '').toLowerCase();

      if (status === 'completed') return 'Trace completed.';
      if (status === 'cancelled') return 'Processing stopped.';
      if (status === 'guardrail') return 'Review needed.';
      if (status === 'failed') return 'Processing needs attention.';
      if (status === 'paused') return 'Ready to continue.';
      if (status === 'queued') return 'Ready to process.';

      if (status === 'running') {
        if (this.request?.lease_active && !this.childLeaseOwned) {
          return 'Processing in another tab.';
        }

        if (/(finaliz|freez|saving final|save.*result|complete locally)/.test(rawMessage)) {
          return 'Saving final result.';
        }

        return 'Tracing transactions.';
      }

      return 'Preparing request.';
    },
    statusDescription() {
      if (this.request?.lease_active && !this.childLeaseOwned) return 'Another tab currently owns the processor lease.';
      if (this.request?.next_action === 'resume') return 'The latest durable checkpoint can be resumed without another charge.';
      if (this.request?.status === 'completed') return 'The result is frozen and available without rerunning Tool 88.';
      return 'The request overview will continue to receive durable status updates.';
    },
  },
  watch: {
    requestUuid: {
      immediate: true,
      handler() {
        this.childLeaseOwned = false;
        this.request = null;
        this.processorMountReady = false;
        this.processorIssue = null;
        this.processorRecoveryInFlight = false;
        this.requestDeleted = false;
        this.requestDeletionMessage = '';
        this.processorDebugAllowed = false;
        this.processorDebugVisible = false;
        this.processorDebugMeta = {};
        this.processorChildDebug = null;
        this.processorDebugCopyStatus = '';
        this.processorStartupMessage = 'Loading the durable request record.';
        this.cancelBatchAdvanceTimer();
        this.batchAdvanceInFlight = false;
        this.batchAdvanceScheduledFor = '';
        this.cancelProcessorMount();
        void this.refreshRequest(true);
      },
    },
  },
  mounted() {
    void this.refreshCurrentUserRole();
    this.startRequestEventListener();
    if (!this.adminMode && typeof window !== 'undefined' && this.requestUuid) {
      window.name = this.inBatchMode
        ? v2BatchClient.batchWindowName(this.batchUuid)
        : this.processorWindowName(this.requestUuid);
    }
    if (this.inBatchMode) {
      this.startBatchHeartbeatTimer();
    }
    this.pollTimer = window.setInterval(() => {
      if (!this.requestDeleted && !this.childLeaseOwned && this.request?.status !== 'completed') {
        void this.refreshRequest(false);
      }
    }, 5000);
  },
  beforeUnmount() {
    if (this.pollTimer) window.clearInterval(this.pollTimer);
    this.pollTimer = null;
    this.stopRequestEventListener();
    this.cancelProcessorMount();
    this.cancelBatchAdvanceTimer();
    this.stopBatchHeartbeatTimer();
    if (this.lifecycleAbortController) {
      this.lifecycleAbortController.abort();
      this.lifecycleAbortController = null;
    }
  },
  errorCaptured(error, instance, info) {
    // Runtime errors must never replace the live visual with the startup screen.
    // Preserve the mounted processor and offer a controlled recovery action.
    this.recordProcessorIssue(error, info || 'component event');
    console.error('V2 processor component error:', error, info, instance);
    return false;
  },
  methods: {
    requestFailureMeansDeleted(error) {
      const reason = String(error?.reasonCode || error?.reason_code || '').trim().toLowerCase();
      const status = Number(error?.httpStatus || 0);
      return status === 410
        || (status === 404 && ['request_not_found', 'request_deleted', 'request_unavailable'].includes(reason))
        || ['request_deleted', 'request_unavailable'].includes(reason);
    },

    startRequestEventListener() {
      if (typeof window === 'undefined') return;

      if (typeof BroadcastChannel === 'function') {
        try {
          this.requestEventChannel = new BroadcastChannel(V2_REQUEST_EVENT_CHANNEL);
          this.requestEventChannel.onmessage = (event) => this.handleExternalRequestEvent(event?.data);
        } catch (error) {
          this.requestEventChannel = null;
          console.debug('Could not start the V2 request event channel.', error);
        }
      }

      this.requestStorageListener = (event) => {
        if (event?.key !== V2_REQUEST_EVENT_STORAGE_KEY || !event.newValue) return;
        try {
          this.handleExternalRequestEvent(JSON.parse(event.newValue));
        } catch (error) {
          console.debug('Could not parse a V2 request storage event.', error);
        }
      };
      window.addEventListener('storage', this.requestStorageListener);
    },

    stopRequestEventListener() {
      if (this.requestEventChannel) {
        try { this.requestEventChannel.close(); } catch (error) { console.debug('Could not close V2 request event channel.', error); }
      }
      this.requestEventChannel = null;

      if (this.requestStorageListener && typeof window !== 'undefined') {
        window.removeEventListener('storage', this.requestStorageListener);
      }
      this.requestStorageListener = null;
    },

    handleExternalRequestEvent(event = {}) {
      const type = String(event?.type || '').trim().toLowerCase();
      const requestUuid = String(event?.requestUuid || event?.request_uuid || '').trim().toLowerCase();
      if (type !== 'request-deleted' || requestUuid !== this.requestUuid.toLowerCase()) return;
      this.markRequestDeleted(event?.message || 'This request was deleted from its history overview.');
    },

    handleRequestDeleted(payload = {}) {
      this.markRequestDeleted(payload?.message || 'This request was deleted and processing has stopped.');
      this.scheduleBatchAdvance('cancelled');
    },

    markRequestDeleted(message = '') {
      if (this.requestDeleted) return;
      this.requestDeleted = true;
      this.requestDeletionMessage = String(message || 'This request was deleted and processing has stopped.');
      this.loading = false;
      this.fetchInFlight = false;
      this.errorMessage = '';
      this.processorIssue = null;
      this.processorRecoveryInFlight = false;
      this.childLeaseOwned = false;
      this.cancelProcessorMount();

      const child = this.$refs.caseBuddyProcessor;
      if (child && typeof child.caseBuddyHandleManagedRequestDeleted === 'function') {
        try {
          child.caseBuddyHandleManagedRequestDeleted({
            request_uuid: this.requestUuid,
            message: this.requestDeletionMessage,
          });
        } catch (error) {
          console.debug('Could not notify Case Buddy Lite of request deletion.', error);
        }
      }

      this.processorMountReady = false;
      this.request = null;
      if (typeof document !== 'undefined') document.title = 'Request deleted · PathTracer';
      this.scheduleBatchAdvance('cancelled');
    },

    closeDeletedProcessorTab() {
      if (typeof window !== 'undefined') {
        try { window.close(); } catch (error) { console.debug('Could not close the processor tab.', error); }
      }
      if (typeof window === 'undefined' || !window.closed) this.returnToAnalysis();
    },

    reconcileProcessorDebugAccess() {
      // Diagnostics require both approvals: the private V2 service must allow
      // them and the current database-verified session role must be staff.
      this.processorDebugAllowed =
        this.processorDebugMeta?.allowed === true
        && this.currentUserIsStaff;

      if (!this.processorDebugAllowed) {
        this.processorDebugVisible = false;
        this.processorChildDebug = null;
      }
    },
    async refreshCurrentUserRole() {
      try {
        const session = await AuthService.getUserRole();
        this.currentUserRole = String(session?.role || '').trim().toLowerCase();
      } catch (error) {
        console.warn('Could not verify the current role for processor diagnostics.', error);
        this.currentUserRole = '';
      }

      this.reconcileProcessorDebugAccess();
      this.applyRequestedProcessorDebugVisibility();
    },
    applyRequestedProcessorDebugVisibility() {
      if (!this.processorDebugAllowed) return;
      const requested = String(this.$route.query.debug || '').trim().toLowerCase();
      if (['1', 'true', 'yes', 'on'].includes(requested)) {
        this.processorDebugVisible = true;
      }
    },
    applyProcessorDebugMetadata(data = {}) {
      const metadata = data?.processor_debug && typeof data.processor_debug === 'object'
        ? data.processor_debug
        : {};
      // Non-sensitive checkpoint ceilings are returned to every processor so
      // frontend compaction stays aligned with the private server limit.
      this.processorDebugMeta = { ...metadata };
      this.reconcileProcessorDebugAccess();
      this.applyRequestedProcessorDebugVisibility();
    },
    toggleProcessorDebug() {
      if (!this.processorDebugAllowed) return;
      this.processorDebugVisible = !this.processorDebugVisible;
      this.processorDebugCopyStatus = '';
      if (this.processorDebugVisible) {
        this.$nextTick(() => this.refreshProcessorDiagnostics());
      } else {
        this.processorChildDebug = null;
      }
    },
    refreshProcessorDiagnostics() {
      if (!this.processorDebugAllowed || !this.processorDebugVisible) return;
      const child = this.$refs.caseBuddyProcessor;
      if (child && typeof child.caseBuddyGetManagedDiagnostics === 'function') {
        try {
          this.processorChildDebug = child.caseBuddyGetManagedDiagnostics();
        } catch (error) {
          this.processorChildDebug = {
            checkpoint: { warning: error?.message || 'Could not read browser diagnostics.' },
          };
        }
      }
    },
    formatBytes(value) {
      const bytes = Number(value);
      if (!Number.isFinite(bytes) || bytes < 0) return '—';
      if (bytes < 1024) return `${Math.round(bytes)} B`;
      if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / 1048576).toFixed(2)} MB`;
    },
    formatMilliseconds(value) {
      const ms = Number(value);
      if (!Number.isFinite(ms) || ms < 0) return '—';
      if (ms < 1000) return `${ms.toFixed(ms < 10 ? 1 : 0)} ms`;
      return `${(ms / 1000).toFixed(2)} s`;
    },
    processorDebugRequestSummary() {
      const request = this.request || {};
      return {
        request_uuid: request.request_uuid || null,
        display_id: request.display_id || null,
        network: request.network || null,
        request_type: request.request_type || null,
        hop_logic: request.hop_logic || null,
        status: request.status || null,
        status_message: request.status_message || null,
        guardrail_reason: request.guardrail_reason || null,
        progress_percent: request.progress_percent ?? null,
        output_row_count: request.output_row_count ?? null,
        endpoint_count: request.endpoint_count ?? null,
        state_version: request.state_version ?? null,
        resume_allowed: request.resume_allowed ?? null,
        lease_active: request.lease_active ?? null,
        lease_expires_at: request.lease_expires_at || null,
        last_heartbeat_at: request.last_heartbeat_at || null,
        last_checkpoint_at: request.last_checkpoint_at || null,
        resume_state_bytes: request.resume_state_bytes ?? null,
        processor_version: request.processor_version || null,
        result_schema_version: request.result_schema_version ?? null,
        result_sha256: request.result_sha256 || null,
        has_resume_state: Boolean(request.resume_state),
        has_result_summary: Boolean(request.result_summary),
        has_visual_snapshot: Boolean(request.visual_snapshot),
      };
    },
    async copyProcessorDiagnostics() {
      if (!this.processorDebugAllowed) return;
      this.refreshProcessorDiagnostics();
      const payload = {
        generatedAtUtc: new Date().toISOString(),
        requestId: this.requestUuid,
        route: this.$route.fullPath,
        server: { ...this.processorDebugMeta },
        request: this.processorDebugRequestSummary(),
        processorIssue: this.processorIssue ? { ...this.processorIssue } : null,
        browser: this.processorChildDebug,
      };
      const text = JSON.stringify(payload, null, 2);
      let copied = false;
      try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          await navigator.clipboard.writeText(text);
          copied = true;
        }
      } catch {
        copied = false;
      }
      if (!copied && typeof document !== 'undefined') {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', 'readonly');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        try { copied = document.execCommand('copy'); } catch { copied = false; }
        area.remove();
      }
      const textBytes = typeof TextEncoder !== 'undefined'
        ? new TextEncoder().encode(text).length
        : unescape(encodeURIComponent(text)).length;
      this.processorDebugCopyStatus = copied
        ? `Diagnostics copied (${this.formatBytes(textBytes)}).`
        : 'Could not copy diagnostics. Use the Case Buddy Lite Debug log button instead.';
    },
    safeProcessorMessage(error, fallback = 'The processor paused before finishing the current step.') {
      const message = String(error?.message || fallback).trim();
      return message.length > 360 ? `${message.slice(0, 357)}…` : message;
    },
    recordProcessorIssue(error, stage = 'processor') {
      const reference = String(error?.requestId || error?.request_id || '').trim();
      this.processorIssue = {
        stage: String(stage || 'processor'),
        message: this.safeProcessorMessage(error),
        reference,
        occurredAt: new Date().toISOString(),
      };
    },
    dismissProcessorIssue() {
      this.processorIssue = null;
    },
    async retryProcessor() {
      if (this.processorRecoveryInFlight) return;
      this.processorRecoveryInFlight = true;
      this.processorIssue = null;
      try {
        await this.$nextTick();
        const child = this.$refs.caseBuddyProcessor;
        if (child && typeof child.caseBuddyRecoverManagedRequest === 'function') {
          await child.caseBuddyRecoverManagedRequest();
          return;
        }

        // The asynchronous child failed before exposing its public recovery
        // method. Remount only after the user explicitly requests recovery.
        this.processorMountGeneration += 1;
        this.processorMountReady = false;
        await this.refreshRequest(true);
      } catch (error) {
        this.recordProcessorIssue(error, 'recovery');
      } finally {
        this.processorRecoveryInFlight = false;
      }
    },
    processorWindowName(requestUuid = '') {
      const safeUuid = String(requestUuid || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '');
      return safeUuid ? `pathtracer-v2-request-${safeUuid}` : 'pathtracer-v2-request';
    },
    reportCategoryKey(entry = {}) {
      const key = String(entry.flag || '').trim().toLowerCase();
      if (['green', 'red', 'blue', 'orange'].includes(key)) return key;
      return entry.pending ? 'orange' : 'green';
    },
    reportCategoryLabel(entry = {}) {
      const labels = {
        green: 'Green flag',
        red: 'Red flag',
        blue: 'Blue flag',
        orange: 'Orange flag',
      };
      return labels[this.reportCategoryKey(entry)] || 'Reviewed ending';
    },
    reportTokenFromText(value = '') {
      const text = String(value ?? '').trim();
      if (!text) return '';

      // Remove numeric values before looking for a symbol so scientific
      // notation such as 5e-8 cannot be mistaken for a token name.
      const withoutAmounts = text.replace(
        /[-+]?(?:(?:\d[\d,]*)(?:\.\d*)?|\.\d+)(?:[eE][-+]?\d+)?/g,
        ' '
      );
      const matches = withoutAmounts
        .toUpperCase()
        .match(/\b[A-Z][A-Z0-9.-]{0,15}\b/g);

      return matches?.length ? matches[matches.length - 1] : '';
    },
    reportEntryToken(entry = {}) {
      const explicit = String(entry.token || '').trim().toUpperCase();
      if (explicit && explicit !== 'UNKNOWN') return explicit;
      return this.reportTokenFromText(entry.fundsFollowed || entry.amount || '');
    },
    reportEntryAmountSource(entry = {}) {
      const candidates = [entry.fundsFollowed, entry.amount, entry.amountNumeric];
      const sourceWithAmount = candidates.find((value) => (
        value !== null
        && value !== undefined
        && String(value).trim() !== ''
        && this.reportExtractDecimal(value) !== ''
      ));
      if (sourceWithAmount !== undefined) return sourceWithAmount;

      const fallback = candidates.find((value) => (
        value !== null
        && value !== undefined
        && String(value).trim() !== ''
      ));
      return fallback ?? '';
    },
    reportPlainDecimal(value) {
      const raw = String(value ?? '')
        .trim()
        .replace(/,/g, '')
        .replace(/\u2212/g, '-');
      const match = raw.match(
        /^([+-]?)(?:(\d+)(?:\.(\d*))?|\.(\d+))(?:[eE]([+-]?\d+))?$/
      );
      if (!match) return '';

      const integerDigits = match[2] || '';
      const fractionDigits = match[3] ?? match[4] ?? '';
      const exponent = Number(match[5] || 0);
      if (!Number.isInteger(exponent) || Math.abs(exponent) > 512) return '';

      const digits = `${integerDigits}${fractionDigits}` || '0';
      const decimalIndex = integerDigits.length + exponent;
      let integerPart = '';
      let fractionPart = '';

      if (decimalIndex <= 0) {
        integerPart = '0';
        fractionPart = `${'0'.repeat(Math.abs(decimalIndex))}${digits}`;
      } else if (decimalIndex >= digits.length) {
        integerPart = `${digits}${'0'.repeat(decimalIndex - digits.length)}`;
      } else {
        integerPart = digits.slice(0, decimalIndex);
        fractionPart = digits.slice(decimalIndex);
      }

      integerPart = integerPart.replace(/^0+(?=\d)/, '') || '0';
      return fractionPart ? `${integerPart}.${fractionPart}` : integerPart;
    },
    reportExtractDecimal(value) {
      if (typeof value === 'number' && !Number.isFinite(value)) return '';
      const match = String(value ?? '').match(REPORT_AMOUNT_PATTERN);
      return match ? this.reportPlainDecimal(match[0]) : '';
    },
    reportDecimalParts(value) {
      const decimal = this.reportExtractDecimal(value);
      if (!decimal) return null;
      const [integer = '0', fraction = ''] = decimal.split('.');
      return {
        integer: integer.replace(/^0+(?=\d)/, '') || '0',
        fraction,
      };
    },
    addReportIntegerStrings(left, right) {
      let leftIndex = left.length - 1;
      let rightIndex = right.length - 1;
      let carry = 0;
      let result = '';

      while (leftIndex >= 0 || rightIndex >= 0 || carry) {
        const leftDigit = leftIndex >= 0 ? Number(left[leftIndex]) : 0;
        const rightDigit = rightIndex >= 0 ? Number(right[rightIndex]) : 0;
        const total = leftDigit + rightDigit + carry;
        result = `${total % 10}${result}`;
        carry = Math.floor(total / 10);
        leftIndex -= 1;
        rightIndex -= 1;
      }

      return result.replace(/^0+(?=\d)/, '') || '0';
    },
    addReportDecimalAmounts(left, right) {
      const leftParts = this.reportDecimalParts(left);
      const rightParts = this.reportDecimalParts(right);
      if (!leftParts) {
        return rightParts
          ? `${rightParts.integer}${rightParts.fraction ? `.${rightParts.fraction}` : ''}`
          : '';
      }
      if (!rightParts) {
        return `${leftParts.integer}${leftParts.fraction ? `.${leftParts.fraction}` : ''}`;
      }

      const scale = Math.max(leftParts.fraction.length, rightParts.fraction.length);
      const leftDigits = `${leftParts.integer}${leftParts.fraction.padEnd(scale, '0')}` || '0';
      const rightDigits = `${rightParts.integer}${rightParts.fraction.padEnd(scale, '0')}` || '0';
      const totalDigits = this.addReportIntegerStrings(leftDigits, rightDigits);

      if (scale === 0) return totalDigits;
      const padded = totalDigits.padStart(scale + 1, '0');
      const integer = padded.slice(0, -scale).replace(/^0+(?=\d)/, '') || '0';
      const fraction = padded.slice(-scale);
      return `${integer}.${fraction}`;
    },
    formatReportNumber(value, token = '') {
      const parts = this.reportDecimalParts(value);
      if (!parts) return '—';

      const symbol = String(token || '').trim().toUpperCase();
      const isStablecoin = REPORT_STABLECOIN_SYMBOLS.has(symbol);
      const isNonZero = /[1-9]/.test(`${parts.integer}${parts.fraction}`);
      const truncatesToZeroAtFour = (
        parts.integer === '0'
        && parts.fraction.slice(0, 4).padEnd(4, '0') === '0000'
      );
      const fractionDigits = isStablecoin
        ? 2
        : (isNonZero && truncatesToZeroAtFour ? 8 : 4);

      const groupedInteger = parts.integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const truncatedFraction = parts.fraction
        .slice(0, fractionDigits)
        .padEnd(fractionDigits, '0');

      // Deliberately use slicing rather than Intl.NumberFormat/toFixed: both
      // round, while PathTracer report amounts must never imply extra funds.
      return `${groupedInteger}.${truncatedFraction}`;
    },
    formatReportEntryAmount(entry = {}) {
      const source = this.reportEntryAmountSource(entry);
      const token = this.reportEntryToken(entry);
      const formatted = this.formatReportNumber(source, token);
      if (formatted === '—') {
        return String(entry.fundsFollowed || entry.amount || '').trim() || '—';
      }
      return `${formatted}${token ? ` ${token}` : ''}`;
    },
    formatReportAmountGroups(entries = [], fallback = '—') {
      const groups = new Map();
      (Array.isArray(entries) ? entries : []).forEach((entry) => {
        const amount = this.reportExtractDecimal(this.reportEntryAmountSource(entry));
        if (!amount) return;
        const token = this.reportEntryToken(entry) || 'Asset';
        groups.set(
          token,
          this.addReportDecimalAmounts(groups.get(token) || '0', amount)
        );
      });

      if (groups.size) {
        return Array.from(groups.entries())
          .sort(([left], [right]) => left.localeCompare(right))
          .map(([token, amount]) => `${this.formatReportNumber(amount, token)} ${token}`)
          .join(' · ');
      }

      const fallbackText = String(fallback ?? '').trim();
      if (!fallbackText || fallbackText === '—') return '—';
      const fallbackToken = this.reportTokenFromText(fallbackText);
      const formattedFallback = this.formatReportNumber(fallbackText, fallbackToken);
      return formattedFallback === '—'
        ? fallbackText
        : `${formattedFallback}${fallbackToken ? ` ${fallbackToken}` : ''}`;
    },
    authHeaders() {
      const token = localStorage.getItem('token') || '';
      return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
    },
    async lifecycleRequest(action, payload = {}) {
      if (this.lifecycleAbortController) {
        this.lifecycleAbortController.abort();
      }
      const controller = new AbortController();
      this.lifecycleAbortController = controller;
      const timeout = window.setTimeout(() => controller.abort(), 20000);

      let response;
      try {
        response = await fetch(this.lifecycleApiUrl, {
          method: 'POST',
          headers: this.authHeaders(),
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal,
          body: JSON.stringify({ action, ...payload }),
        });
      } catch (error) {
        if (error?.name === 'AbortError') {
          throw new Error('The request service did not respond within 20 seconds.');
        }
        throw error;
      } finally {
        window.clearTimeout(timeout);
        if (this.lifecycleAbortController === controller) {
          this.lifecycleAbortController = null;
        }
      }

      const requestId = String(response.headers?.get?.('X-Request-ID') || '').trim();
      const text = await response.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (error) {
        data = null;
      }
      if (!response.ok || !data || data.status === 'error') {
        const failure = new Error(data?.message || `Request service returned HTTP ${response.status}.`);
        failure.reasonCode = data?.reason_code || '';
        failure.httpStatus = response.status;
        failure.requestId = requestId || String(data?.request_id || '').trim();
        throw failure;
      }
      return { ...data, __requestId: requestId || String(data?.request_id || '').trim() };
    },
    async refreshRequest(showLoading = false) {
      if (!this.requestUuid || this.fetchInFlight || this.requestDeleted) return;
      this.fetchInFlight = true;
      if (showLoading) this.loading = true;
      this.errorMessage = '';
      try {
        const data = await this.lifecycleRequest('get', {
          request_uuid: this.requestUuid,
          include_resume: true,
          include_events: false,
          audit_view: this.adminMode && !this.adminViewAudited,
        });
        this.applyProcessorDebugMetadata(data);
        this.request = data.request || null;
        if (this.request && data?.owner) {
          this.request.owner_user_id = Number(data.owner.user_id || 0) || null;
          this.request.owner_username = String(data.owner.username || '');
        }
        if (this.adminMode) this.adminViewAudited = true;
        if (!this.request) throw new Error('The request was not returned by the server.');
        document.title = `${this.adminMode ? 'Staff review' : this.statusLabel} · Request ${this.request.display_id || this.requestUuid.slice(0, 8)} · PathTracer`;
        this.scheduleProcessorMount();
        this.scheduleBatchAdvanceForRequest(this.request);
      } catch (error) {
        if ((this.request || this.processorMountReady) && this.requestFailureMeansDeleted(error)) {
          this.markRequestDeleted('This request was deleted from history. Its processor lease and pending work were stopped.');
          return;
        }
        if (this.request) this.recordProcessorIssue(error, 'request refresh');
        else this.errorMessage = error?.message || 'Could not load the request.';
        if (error?.httpStatus === 401) this.$router.push({ name: 'login' }).catch(() => {});
      } finally {
        this.loading = false;
        this.fetchInFlight = false;
      }
    },
    cancelProcessorMount() {
      if (this.processorMountTimer && typeof window !== 'undefined') {
        window.clearTimeout(this.processorMountTimer);
      }
      this.processorMountTimer = null;
    },
    scheduleProcessorMount() {
      if (!this.request || this.processorMountReady || this.reportOnlyView) return;
      this.cancelProcessorMount();
      this.processorStartupMessage = 'Request loaded. Preparing the Case Buddy Lite processor.';

      const mount = () => {
        this.processorMountTimer = window.setTimeout(() => {
          this.processorMountTimer = null;
          this.processorMountReady = true;
          this.processorStartupMessage = 'Processor workspace is loading.';
        }, 0);
      };

      this.$nextTick(() => {
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(mount);
        } else {
          mount();
        }
      });
    },
    requestStateSignature(request = null) {
      if (!request || typeof request !== 'object') return '';
      return [
        String(request.request_uuid || ''),
        Number(request.state_version || 0),
        String(request.status || ''),
        Number(request.progress_percent || 0).toFixed(2),
        request.lease_active ? '1' : '0',
        String(request.lease_expires_at || ''),
        String(request.updated_at || ''),
        String(request.result_sha256 || ''),
      ].join('|');
    },
    handleRequestUpdated(request) {
      if (!request) return;
      const merged = { ...(this.request || {}), ...request };
      if (this.requestStateSignature(merged) === this.requestStateSignature(this.request)) {
        return;
      }
      this.request = merged;
      this.refreshProcessorDiagnostics();
      if (['completed', 'paused', 'failed', 'guardrail', 'cancelled'].includes(this.request.status)) {
        this.childLeaseOwned = false;
      }
      this.scheduleBatchAdvanceForRequest(this.request);
    },
    handleManagedReady() {
      this.processorIssue = null;
      this.refreshProcessorDiagnostics();
      this.processorStartupMessage = this.autoStart
        ? 'Processor loaded. Acquiring the request lease.'
        : 'Processor workspace is ready.';
    },
    handleLeaseAcquired(payload) {
      this.processorIssue = null;
      this.refreshProcessorDiagnostics();
      this.childLeaseOwned = true;
      if (payload?.request) this.handleRequestUpdated(payload.request);
    },
    handleLeaseConflict(payload) {
      this.childLeaseOwned = false;
      const error = new Error(payload?.message || 'Another tab owns the active processing lease.');
      this.recordProcessorIssue(error, 'processing lease');
      void this.refreshRequest(false);
    },
    handleComplete(payload) {
      this.processorIssue = null;
      this.childLeaseOwned = false;
      if (payload?.request) this.handleRequestUpdated(payload.request);
      this.scheduleBatchAdvance('completed');
      void this.refreshRequest(false);
    },
    handleGuardrail() {
      this.childLeaseOwned = false;
      this.scheduleBatchAdvance('guardrail');
      void this.refreshRequest(false);
    },
    handleProcessorError(error) {
      if (this.requestFailureMeansDeleted(error)) {
        this.markRequestDeleted('This request was deleted from history. Processing has stopped.');
        return;
      }
      this.recordProcessorIssue(error, error?.stage || 'processor');
    },
    batchTerminalStatus(status) {
      return ['completed', 'failed', 'guardrail', 'cancelled'].includes(
        String(status || '').toLowerCase()
      );
    },
    scheduleBatchAdvanceForRequest(request) {
      if (!this.inBatchMode || !this.batchTerminalStatus(request?.status)) return;
      this.scheduleBatchAdvance(request.status);
    },
    scheduleBatchAdvance(status = '') {
      if (!this.inBatchMode) return;
      const signature = [
        this.requestUuid,
        String(status || this.request?.status || ''),
        Number(this.request?.state_version || 0),
      ].join('|');
      if (this.batchAdvanceScheduledFor === signature || this.batchAdvanceInFlight) return;
      this.batchAdvanceScheduledFor = signature;
      this.cancelBatchAdvanceTimer();
      this.batchAdvanceTimer = window.setTimeout(() => {
        this.batchAdvanceTimer = null;
        void this.advanceBatchQueue();
      }, 650);
    },
    cancelBatchAdvanceTimer() {
      if (this.batchAdvanceTimer && typeof window !== 'undefined') {
        window.clearTimeout(this.batchAdvanceTimer);
      }
      this.batchAdvanceTimer = null;
    },
    async advanceBatchQueue() {
      if (!this.inBatchMode || this.batchAdvanceInFlight) return;
      this.batchAdvanceInFlight = true;
      try {
        await v2BatchClient.ensureLease(this.batchUuid);
        this.startBatchHeartbeatTimer();
        const result = await v2BatchClient.advance(this.batchUuid);
        const nextUuid = String(result?.request?.request_uuid || '').trim();
        const nextItemId = Number(result?.item?.id || 0);

        if (nextUuid) {
          if (nextUuid.toLowerCase() === this.requestUuid.toLowerCase()) {
            // The lifecycle row may still be committing. Reload rather than
            // remounting the same processor in a tight loop.
            window.setTimeout(() => void this.refreshRequest(false), 1200);
            return;
          }

          await this.$router.replace({
            name: 'AnalysisToolV2Processor',
            params: { requestUuid: nextUuid },
            query: v2BatchClient.requestRouteQuery(
              this.batchUuid,
              { id: nextItemId },
              result?.next_action || 'start'
            ),
          });
          return;
        }

        await this.$router.replace({
          name: 'AnalysisToolV2BatchProcessor',
          params: { batchUuid: this.batchUuid },
        });
      } catch (error) {
        const reason = String(error?.response?.data?.reason_code || '');
        if (reason === 'batch_lease_active_elsewhere') {
          this.recordProcessorIssue(
            new Error('Another tab is controlling this CSV batch. Open the batch queue to continue there.'),
            'batch processor lease'
          );
        } else {
          this.recordProcessorIssue(
            new Error(error?.response?.data?.message || error?.message || 'The CSV batch could not continue.'),
            'batch advance'
          );
        }
      } finally {
        this.batchAdvanceInFlight = false;
      }
    },
    startBatchHeartbeatTimer() {
      if (!this.inBatchMode || this.batchHeartbeatTimer) return;
      this.batchHeartbeatTimer = window.setInterval(async () => {
        try {
          await v2BatchClient.heartbeat(this.batchUuid);
        } catch (error) {
          const reason = String(error?.response?.data?.reason_code || '');
          if (['batch_lease_expired', 'batch_lease_mismatch'].includes(reason)) {
            try {
              await v2BatchClient.ensureLease(this.batchUuid);
              return;
            } catch {
              // The next terminal transition will surface the actionable error.
            }
          }
          this.stopBatchHeartbeatTimer();
        }
      }, 60000);
    },
    stopBatchHeartbeatTimer() {
      if (this.batchHeartbeatTimer && typeof window !== 'undefined') {
        window.clearInterval(this.batchHeartbeatTimer);
      }
      this.batchHeartbeatTimer = null;
    },
    returnToBatch() {
      if (!this.inBatchMode) return;
      this.$router.push({
        name: 'AnalysisToolV2BatchProcessor',
        params: { batchUuid: this.batchUuid },
      }).catch(() => this.$router.push(`/analysis-v2/batch/${this.batchUuid}`));
    },
    returnToAnalysis() {
      if (this.adminMode) {
        if (window.opener && !window.opener.closed) {
          window.opener.focus();
          window.close();
          return;
        }
        this.$router.back();
        return;
      }
      if (this.inBatchMode) {
        this.returnToBatch();
        return;
      }
      if (window.opener && !window.opener.closed) {
        window.opener.focus();
        window.close();
        return;
      }
      this.$router.push({ name: 'AnalysisToolV2' }).catch(() => this.$router.push('/analysis-v2'));
    },
  },
};
</script>

<style scoped>
.v2-processor-page {
  max-width: 1560px;
  margin: 0 auto;
  padding: 0.65rem 1.25rem 2rem;
  color: #0f172a;
  min-height: 100vh;
  background:
    radial-gradient(circle at 8% 0%, rgba(37, 99, 235, 0.08), transparent 32rem),
    linear-gradient(180deg, #f8fafc 0%, #f4f7fb 100%);
}
.v2-processor-panel.v2-processor-panel--deleted {
  max-width: 760px;
  margin: 4rem auto;
  padding: 1.35rem;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  border-color: #fed7aa;
  background: linear-gradient(145deg, #fff7ed, #ffffff);
}
.v2-processor-panel.v2-processor-panel--deleted h2 {
  margin: 0.2rem 0 0.45rem;
}
.v2-processor-panel.v2-processor-panel--deleted p {
  margin: 0.25rem 0;
}

.v2-processor-batch-context {
  max-width: 1560px;
  margin: 0 auto 0.75rem;
  padding: 0.8rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 1px solid #bfdbfe;
  border-radius: 14px;
  background: linear-gradient(135deg, #eff6ff, #ffffff);
}
.v2-processor-batch-context strong,
.v2-processor-batch-context span { display: block; }
.v2-processor-batch-context span { margin-top: 0.15rem; color: #64748b; font-size: 0.86rem; }

.v2-processor-header,
.v2-processor-status,
.v2-processor-panel {
  border: 1px solid #dbe4ef;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 12px 36px rgba(15, 23, 42, 0.06);
}
.v2-processor-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  padding: 1rem 1.1rem;
  margin-top: 1rem;
}
.v2-processor-header--context {
  align-items: center;
}
.v2-processor-eyebrow {
  margin: 0 0 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.75rem;
  font-weight: 800;
  color: #2563eb;
}
.v2-processor-header h1 { margin: 0; }
.v2-processor-subtitle { margin: 0.5rem 0 0; color: #64748b; max-width: 820px; }
.v2-processor-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.v2-processor-button {
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  padding: 0.65rem 0.9rem;
  cursor: pointer;
}
.v2-processor-button:disabled { opacity: 0.55; cursor: not-allowed; }
.v2-processor-status {
  padding: 0.9rem 1.1rem;
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  border-left: 5px solid #94a3b8;
}
.v2-processor-workspace {
  min-width: 0;
}
.v2-processor-status__primary {
  min-width: 0;
}
.v2-processor-status strong { display: block; }
.v2-processor-status span { color: #64748b; }
.v2-processor-status__meta { display: flex; align-items: center; gap: 0.8rem; flex-wrap: wrap; font-size: 0.85rem; }
.v2-processor-status--running { border-left-color: #2563eb; }
.v2-processor-status--complete { border-left-color: #16a34a; }
.v2-processor-status--warning { border-left-color: #f97316; }
.v2-processor-status--paused { border-left-color: #a855f7; }
.v2-processor-panel { padding: 1.25rem; }
.v2-processor-panel--error { border-color: #fecaca; background: #fff7f7; }
.v2-processor-panel--warning {
  border-color: #fed7aa;
  background: #fffaf2;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  margin-top: 1rem;
}
.v2-processor-panel--warning h2 { margin: 0.1rem 0 0.35rem; }
.v2-processor-panel--warning p { margin: 0; color: #64748b; }
.v2-processor-reference {
  margin-top: 0.55rem !important;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.78rem;
  color: #9a3412 !important;
}
.v2-processor-button--primary { background: #2563eb; border-color: #2563eb; color: #fff; }
.v2-processor-button--debug {
  background: #111827;
  border-color: #111827;
  color: #fff;
}
.v2-processor-panel--debug {
  border-color: #94a3b8;
  background: #f8fafc;
  margin-top: 1rem;
}
.v2-processor-debug__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}
.v2-processor-debug__header h2 { margin: 0.1rem 0 0.35rem; }
.v2-processor-debug__header p { margin: 0; color: #64748b; }
.v2-processor-debug__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.65rem;
  margin-top: 1rem;
}
.v2-processor-debug__grid > div {
  border: 1px solid #dbe4ef;
  border-radius: 10px;
  padding: 0.7rem;
  background: #fff;
  min-width: 0;
}
.v2-processor-debug__grid span {
  display: block;
  color: #64748b;
  font-size: 0.75rem;
}
.v2-processor-debug__grid strong {
  display: block;
  margin-top: 0.25rem;
  overflow-wrap: anywhere;
}
.v2-processor-debug__details { margin-top: 1rem; }
.v2-processor-debug__details h3 { margin: 0 0 0.5rem; }
.v2-processor-debug__status,
.v2-processor-debug__warning {
  margin: 0.8rem 0 0;
  border-radius: 10px;
  padding: 0.7rem 0.8rem;
}
.v2-processor-debug__status { background: #dbeafe; color: #1e3a8a; }
.v2-processor-debug__warning { background: #ffedd5; color: #9a3412; }
.v2-processor-debug__table { width: 100%; border-collapse: collapse; margin-top: 0.65rem; background: #fff; }
.v2-processor-debug__table th,
.v2-processor-debug__table td { padding: 0.55rem; border-bottom: 1px solid #e2e8f0; text-align: left; }
.v2-processor-boot {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.11);
}
.v2-processor-boot__accent {
  height: 6px;
  background: linear-gradient(90deg, #1d4ed8, #0ea5e9, #22c55e);
}
.v2-processor-boot__content {
  display: grid;
  grid-template-columns: 58px minmax(0, 1fr);
  gap: 1rem 1.2rem;
  align-items: start;
  padding: clamp(1.4rem, 4vw, 2.5rem);
}
.v2-processor-boot__content h2 { margin: 0.1rem 0 0.55rem; font-size: clamp(1.45rem, 3vw, 2rem); }
.v2-processor-boot__content p { margin: 0; color: #64748b; line-height: 1.65; }
.v2-processor-boot__spinner {
  width: 48px;
  height: 48px;
  border-radius: 999px;
  border: 5px solid #dbeafe;
  border-top-color: #2563eb;
  animation: v2-processor-spin 0.85s linear infinite;
}
.v2-processor-boot__steps {
  grid-column: 1 / -1;
  display: grid;
  gap: 0.65rem;
  list-style: none;
  padding: 0;
  margin: 0.35rem 0 0;
}
.v2-processor-boot__steps li { display: flex; align-items: center; gap: 0.7rem; color: #64748b; }
.v2-processor-boot__steps li span {
  width: 11px;
  height: 11px;
  border-radius: 999px;
  background: #cbd5e1;
  flex: 0 0 auto;
}
.v2-processor-boot__steps li.is-complete { color: #166534; }
.v2-processor-boot__steps li.is-complete span { background: #22c55e; box-shadow: 0 0 0 5px rgba(34, 197, 94, 0.12); }
.v2-processor-boot__steps li.is-active { color: #1d4ed8; font-weight: 700; }
.v2-processor-boot__steps li.is-active span { background: #2563eb; box-shadow: 0 0 0 5px rgba(37, 99, 235, 0.12); }
.v2-processor-boot__fine { grid-column: 1 / -1; font-size: 0.84rem; }
.v2-processor-boot--compact { margin-bottom: 0; }
@keyframes v2-processor-spin { to { transform: rotate(360deg); } }
.v2-processor-report {
  border: 1px solid #dbe4ef;
  border-radius: 18px;
  background: #fff;
  padding: 1.2rem;
  margin-top: 1rem;
}
.v2-processor-report h2 { margin: 0.1rem 0 0.35rem; }
.v2-processor-report__metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
  margin: 1rem 0;
}
.v2-processor-report__metrics div { border: 1px solid #e2e8f0; border-radius: 12px; padding: 0.8rem; }
.v2-processor-report__metrics span { display: block; color: #64748b; font-size: 0.8rem; }
.v2-processor-report__metrics strong { display: block; margin-top: 0.25rem; }
.v2-processor-report__table-wrap { overflow-x: auto; }
.v2-processor-report__table { width: 100%; border-collapse: collapse; }
.v2-processor-report__table th, .v2-processor-report__table td { padding: 0.65rem; border-bottom: 1px solid #e2e8f0; text-align: left; }
.v2-processor-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.82rem; }
.v2-report-category {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  white-space: nowrap;
}
.v2-report-category__flag {
  position: relative;
  width: 24px;
  height: 18px;
  border-left: 2px solid #475569;
  flex: 0 0 auto;
}
.v2-report-category__flag::after {
  content: '';
  position: absolute;
  left: 2px;
  top: 1px;
  width: 17px;
  height: 11px;
  border-radius: 2px 5px 5px 2px;
  background: #94a3b8;
  clip-path: polygon(0 0, 100% 12%, 86% 100%, 0 82%);
}
.v2-report-category--green .v2-report-category__flag::after { background: #22c55e; }
.v2-report-category--red .v2-report-category__flag::after { background: #ef4444; }
.v2-report-category--blue .v2-report-category__flag::after { background: #3b82f6; }
.v2-report-category--orange .v2-report-category__flag::after { background: #f97316; }
@media (max-width: 760px) {
  .v2-processor-header,
  .v2-processor-status,
  .v2-processor-panel--warning,
  .v2-processor-debug__header { flex-direction: column; align-items: stretch; }
  .v2-processor-boot__content { grid-template-columns: 1fr; }
  .v2-processor-boot__spinner { width: 42px; height: 42px; }
  .v2-processor-boot__steps,
  .v2-processor-boot__fine { grid-column: 1; }
}
</style>
